import { emitAuthTelemetry, getAuthTelemetryTabId } from "./authTelemetry";

export const AUTH_STORAGE_KEYS = {
    accessToken: "access_token",
    refreshToken: "refresh_token",
    tokenExpiresAt: "token_expires_at",
    tokenIssuedAt: "token_issued_at",
    userInfo: "user_info",
    activeOrganizationId: "active_organization_id",
};

export const AUTH_SESSION_META_KEY = "flymeta.auth.session_meta";
export const AUTH_BROADCAST_CHANNEL_NAME = "flymeta.auth.sync";

const AUTH_STATE_EVENT_TYPE = "flymeta.auth.snapshot";
const AUTH_STATE_KEYS = new Set([...Object.values(AUTH_STORAGE_KEYS), AUTH_SESSION_META_KEY]);
const LOGGED_OUT_STATUS = "anonymous";
const AUTHENTICATED_STATUS = "authenticated";

let broadcastChannel = null;
let syncStarted = false;
let inMemorySnapshot = null;
const listeners = new Set();

function canUseWindow() {
    return typeof window !== "undefined";
}

function canUseStorage() {
    return canUseWindow() && typeof localStorage !== "undefined" && typeof sessionStorage !== "undefined";
}

function createEpoch() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseJson(value, fallback = null) {
    if (!value) {
        return fallback;
    }
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

function parseInteger(value) {
    const parsed = Number.parseInt(value || "", 10);
    return Number.isFinite(parsed) ? parsed : null;
}

function getStorageName(storage) {
    if (!canUseStorage()) {
        return "memory";
    }
    return storage === localStorage ? "localStorage" : "sessionStorage";
}

function getStorageByName(storageName) {
    if (!canUseStorage()) {
        return null;
    }
    return storageName === "sessionStorage" ? sessionStorage : localStorage;
}

export function getPreferredAuthStorage() {
    if (!canUseStorage()) {
        return null;
    }
    if (localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken) || localStorage.getItem(AUTH_STORAGE_KEYS.accessToken)) {
        return localStorage;
    }
    if (sessionStorage.getItem(AUTH_STORAGE_KEYS.refreshToken) || sessionStorage.getItem(AUTH_STORAGE_KEYS.accessToken)) {
        return sessionStorage;
    }
    return localStorage;
}

function readMetaFromStorage() {
    if (!canUseStorage()) {
        return null;
    }
    const parsed = parseJson(localStorage.getItem(AUTH_SESSION_META_KEY));
    return normalizeSessionMeta(parsed);
}

function normalizeSessionMeta(parsed) {
    const sessionEpoch = parsed?.sessionEpoch || parsed?.epoch || parsed?.session_epoch;
    if (!parsed || typeof parsed !== "object" || !sessionEpoch) {
        return null;
    }
    const rawStatus = parsed.status || parsed.authStatus;
    const status = rawStatus === LOGGED_OUT_STATUS || rawStatus === "logged_out"
        ? LOGGED_OUT_STATUS
        : rawStatus === "degraded" ? "degraded" : AUTHENTICATED_STATUS;
    const revision = Number.isFinite(Number(parsed.revision ?? parsed.rev)) ? Number(parsed.revision ?? parsed.rev) : 0;
    return {
        sessionEpoch: String(sessionEpoch),
        revision,
        status,
        logoutTombstone: normalizeLogoutTombstone(parsed.logoutTombstone) || (status === LOGGED_OUT_STATUS ? { sessionEpoch: String(sessionEpoch), revision } : null),
        updatedAt: parsed.updatedAt || null,
    };
}

function normalizeLogoutTombstone(value) {
    if (!value || typeof value !== "object" || !value.sessionEpoch || !Number.isFinite(Number(value.revision))) {
        return null;
    }
    return { sessionEpoch: String(value.sessionEpoch), revision: Number(value.revision) };
}

function deriveLegacyMeta(snapshot) {
    if (!snapshot.accessToken && !snapshot.refreshToken) {
        return null;
    }
    return {
        sessionEpoch: `legacy.${snapshot.tokenIssuedAt || snapshot.tokenExpiresAt || "unknown"}`,
        revision: 0,
        status: AUTHENTICATED_STATUS,
        logoutTombstone: null,
        updatedAt: null,
    };
}

function readSnapshotFromStorage() {
    if (!canUseStorage()) {
        return inMemorySnapshot || createEmptySnapshot();
    }

    const storage = getPreferredAuthStorage();
    const snapshot = {
        accessToken: storage?.getItem(AUTH_STORAGE_KEYS.accessToken) || null,
        refreshToken: storage?.getItem(AUTH_STORAGE_KEYS.refreshToken) || null,
        tokenExpiresAt: storage?.getItem(AUTH_STORAGE_KEYS.tokenExpiresAt) || null,
        tokenIssuedAt: storage?.getItem(AUTH_STORAGE_KEYS.tokenIssuedAt) || null,
        userInfo: storage?.getItem(AUTH_STORAGE_KEYS.userInfo) || null,
        activeOrganizationId: storage?.getItem(AUTH_STORAGE_KEYS.activeOrganizationId) || null,
        storageName: getStorageName(storage),
        meta: readMetaFromStorage(),
    };
    snapshot.meta = snapshot.meta || deriveLegacyMeta(snapshot);
    snapshot.status = snapshot.meta?.status || (snapshot.accessToken || snapshot.refreshToken ? AUTHENTICATED_STATUS : LOGGED_OUT_STATUS);
    return snapshot;
}

function createEmptySnapshot(meta = null) {
    return {
        accessToken: null,
        refreshToken: null,
        tokenExpiresAt: null,
        tokenIssuedAt: null,
        userInfo: null,
        activeOrganizationId: null,
        storageName: "memory",
        meta,
        status: meta?.status || LOGGED_OUT_STATUS,
    };
}

function snapshotHasNewerMeta(candidate, current) {
    if (!candidate?.meta) {
        return false;
    }
    if (!current?.meta) {
        return true;
    }
    if (candidate.meta.sessionEpoch !== current.meta.sessionEpoch) {
        return candidate.meta.status === AUTHENTICATED_STATUS && current.meta.status !== AUTHENTICATED_STATUS;
    }
    if (candidate.meta.revision > current.meta.revision) {
        return true;
    }
    return candidate.meta.revision === current.meta.revision && candidate.meta.status === LOGGED_OUT_STATUS && current.meta.status !== LOGGED_OUT_STATUS;
}

function shouldAcceptIncomingSnapshot(incoming, current, action) {
    if (!incoming?.meta) {
        return false;
    }
    if (!current?.meta) {
        return true;
    }
    if (incoming.meta.sessionEpoch === current.meta.sessionEpoch) {
        if (incoming.meta.revision > current.meta.revision) {
            return true;
        }
        return incoming.meta.revision === current.meta.revision && incoming.meta.status === LOGGED_OUT_STATUS;
    }
    if (action === "login") {
        return true;
    }
    return current.status !== AUTHENTICATED_STATUS && incoming.meta.status === AUTHENTICATED_STATUS;
}

function getCurrentSnapshotWithoutStorageRefresh() {
    if (inMemorySnapshot) {
        return inMemorySnapshot;
    }
    inMemorySnapshot = readSnapshotFromStorage();
    return inMemorySnapshot;
}

export function readAuthSnapshot() {
    const persistedSnapshot = readSnapshotFromStorage();
    const currentSnapshot = getCurrentSnapshotWithoutStorageRefresh();

    if (snapshotHasNewerMeta(persistedSnapshot, currentSnapshot)) {
        inMemorySnapshot = persistedSnapshot;
        notifyAuthStateListeners(persistedSnapshot, { source: "storage-read", action: "hydrate" });
        return persistedSnapshot;
    }

    if (currentSnapshot?.meta && persistedSnapshot?.meta && currentSnapshot.meta.sessionEpoch === persistedSnapshot.meta.sessionEpoch) {
        return currentSnapshot.meta.revision >= persistedSnapshot.meta.revision ? currentSnapshot : persistedSnapshot;
    }

    if (currentSnapshot?.status === AUTHENTICATED_STATUS && currentSnapshot?.accessToken) {
        return currentSnapshot;
    }

    inMemorySnapshot = persistedSnapshot;
    return persistedSnapshot;
}

export function getAccessToken() {
    return readAuthSnapshot().accessToken;
}

export function getRefreshToken() {
    return readAuthSnapshot().refreshToken;
}

export function getAuthHeader() {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export function isTokenFresh(expiresAt) {
    const expiry = parseInteger(expiresAt);
    return Boolean(expiry && expiry > Math.floor(Date.now() / 1000));
}

export function hasFreshAccessToken(snapshot = readAuthSnapshot()) {
    return Boolean(snapshot.accessToken && isTokenFresh(snapshot.tokenExpiresAt));
}

export function hasAuthGenerationAdvanced(beforeSnapshot, afterSnapshot = readAuthSnapshot()) {
    if (!afterSnapshot?.accessToken || !afterSnapshot?.refreshToken) {
        return false;
    }
    if (!beforeSnapshot) {
        return hasFreshAccessToken(afterSnapshot);
    }
    if (beforeSnapshot.meta?.sessionEpoch && afterSnapshot.meta?.sessionEpoch && beforeSnapshot.meta.sessionEpoch === afterSnapshot.meta.sessionEpoch) {
        return afterSnapshot.meta.revision > (beforeSnapshot.meta.revision || 0);
    }
    return afterSnapshot.accessToken !== beforeSnapshot.accessToken || afterSnapshot.tokenIssuedAt !== beforeSnapshot.tokenIssuedAt;
}

export function toRefreshResponse(snapshot = readAuthSnapshot()) {
    if (!snapshot.accessToken || !snapshot.refreshToken || !snapshot.tokenExpiresAt) {
        return null;
    }
    return {
        access_token: snapshot.accessToken,
        refresh_token: snapshot.refreshToken,
        expires_at: snapshot.tokenExpiresAt,
    };
}

function buildNextMeta(action, currentSnapshot) {
    if (action === "login") {
        return {
            sessionEpoch: createEpoch(),
            revision: 1,
            status: AUTHENTICATED_STATUS,
            logoutTombstone: null,
            updatedAt: new Date().toISOString(),
        };
    }

    const currentMeta = currentSnapshot?.meta || deriveLegacyMeta(currentSnapshot || createEmptySnapshot()) || {
        sessionEpoch: createEpoch(),
        revision: 0,
        status: AUTHENTICATED_STATUS,
        logoutTombstone: null,
        updatedAt: null,
    };
    const revision = Number(currentMeta.revision || 0) + 1;

    return {
        sessionEpoch: currentMeta.sessionEpoch,
        revision,
        status: action === "logout" ? LOGGED_OUT_STATUS : AUTHENTICATED_STATUS,
        logoutTombstone: action === "logout" ? { sessionEpoch: currentMeta.sessionEpoch, revision } : null,
        updatedAt: new Date().toISOString(),
    };
}

function snapshotForBroadcast(snapshot) {
    return {
        accessToken: snapshot.accessToken,
        refreshToken: snapshot.refreshToken,
        tokenExpiresAt: snapshot.tokenExpiresAt,
        tokenIssuedAt: snapshot.tokenIssuedAt,
        userInfo: snapshot.userInfo,
        activeOrganizationId: snapshot.activeOrganizationId,
    };
}

function writeSnapshotToStorage(snapshot, action) {
    if (!canUseStorage()) {
        return true;
    }

    const storage = getStorageByName(snapshot.storageName) || localStorage;
    try {
        if (action === "login") {
            Object.values(AUTH_STORAGE_KEYS).forEach((key) => {
                localStorage.removeItem(key);
                sessionStorage.removeItem(key);
            });
        }

        if (snapshot.status === LOGGED_OUT_STATUS) {
            Object.values(AUTH_STORAGE_KEYS).forEach((key) => {
                localStorage.removeItem(key);
                sessionStorage.removeItem(key);
            });
        } else {
            storage.setItem(AUTH_STORAGE_KEYS.accessToken, snapshot.accessToken);
            storage.setItem(AUTH_STORAGE_KEYS.refreshToken, snapshot.refreshToken);
            storage.setItem(AUTH_STORAGE_KEYS.tokenExpiresAt, String(snapshot.tokenExpiresAt));
            storage.setItem(AUTH_STORAGE_KEYS.tokenIssuedAt, String(snapshot.tokenIssuedAt));
            if (snapshot.userInfo) {
                storage.setItem(AUTH_STORAGE_KEYS.userInfo, snapshot.userInfo);
            }
            if (snapshot.activeOrganizationId) {
                storage.setItem(AUTH_STORAGE_KEYS.activeOrganizationId, String(snapshot.activeOrganizationId));
            }
        }

        localStorage.setItem(AUTH_SESSION_META_KEY, JSON.stringify(snapshot.meta));
        return true;
    } catch (error) {
        emitAuthTelemetry({
            correlationId: snapshot.correlationId,
            entrypoint: snapshot.entrypoint || action,
            stage: "persist",
            outcome: "failure",
            category: "local_degraded",
            error,
        });
        return false;
    }
}

function getBroadcastChannel() {
    if (!canUseWindow() || typeof BroadcastChannel === "undefined") {
        return null;
    }
    if (!broadcastChannel) {
        broadcastChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL_NAME);
        broadcastChannel.onmessage = (event) => handleIncomingAuthMessage(event.data);
    }
    return broadcastChannel;
}

function broadcastAuthState(snapshot, action) {
    const channel = getBroadcastChannel();
    if (!channel) {
        return true;
    }

    try {
        channel.postMessage({
            type: AUTH_STATE_EVENT_TYPE,
            meta: snapshot.meta,
            snapshot: snapshotForBroadcast(snapshot),
        });
        return true;
    } catch (error) {
        emitAuthTelemetry({
            correlationId: snapshot.correlationId,
            entrypoint: snapshot.entrypoint || action,
            stage: "broadcast",
            outcome: "failure",
            category: "local_degraded",
            error,
        });
        return false;
    }
}

function notifyAuthStateListeners(snapshot, event) {
    listeners.forEach((listener) => {
        try {
            listener(snapshot, event);
        } catch {
            // A broken subscriber must not block auth state convergence.
        }
    });
}

function commitSnapshot(snapshot, action, options = {}) {
    const currentSnapshot = getCurrentSnapshotWithoutStorageRefresh();
    if (action !== "login" && !shouldAcceptIncomingSnapshot(snapshot, currentSnapshot, action)) {
        return { snapshot: currentSnapshot, persisted: true, broadcasted: true, accepted: false };
    }

    inMemorySnapshot = snapshot;
    notifyAuthStateListeners(snapshot, { source: "local", action });

    const persisted = options.persist === false ? true : writeSnapshotToStorage(snapshot, action);
    const broadcasted = options.broadcast === false ? true : broadcastAuthState(snapshot, action);
    return { snapshot, persisted, broadcasted, accepted: true, degraded: !persisted || !broadcasted };
}

export function writeLoginAuthState(response, { remember = true, correlationId, entrypoint = "login" } = {}) {
    const userInfo = response.user ? JSON.stringify(response.user) : null;
    const activeOrganizationId = response.active_organization_id || null;
    const snapshot = {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        tokenExpiresAt: String(response.expires_at),
        tokenIssuedAt: String(Math.floor(Date.now() / 1000)),
        userInfo,
        activeOrganizationId,
        storageName: remember ? "localStorage" : "sessionStorage",
        status: AUTHENTICATED_STATUS,
        meta: buildNextMeta("login"),
        correlationId,
        entrypoint,
    };
    return commitSnapshot(snapshot, "login");
}

export function writeRefreshAuthState(response, { correlationId, entrypoint = "refresh" } = {}) {
    const currentSnapshot = readAuthSnapshot();
    const snapshot = {
        ...currentSnapshot,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        tokenExpiresAt: String(response.expires_at),
        tokenIssuedAt: String(Math.floor(Date.now() / 1000)),
        storageName: currentSnapshot.storageName === "sessionStorage" ? "sessionStorage" : "localStorage",
        status: AUTHENTICATED_STATUS,
        meta: buildNextMeta("refresh", currentSnapshot),
        correlationId,
        entrypoint,
    };
    return commitSnapshot(snapshot, "refresh");
}

export function writeUserInfoAuthState(userInfo, { correlationId, entrypoint = "user-info" } = {}) {
    const currentSnapshot = readAuthSnapshot();
    if (!currentSnapshot.accessToken && !currentSnapshot.refreshToken) {
        return { snapshot: currentSnapshot, persisted: true, broadcasted: true, accepted: false };
    }
    const snapshot = {
        ...currentSnapshot,
        userInfo: JSON.stringify(userInfo),
        status: AUTHENTICATED_STATUS,
        meta: buildNextMeta("user-info", currentSnapshot),
        correlationId,
        entrypoint,
    };
    return commitSnapshot(snapshot, "refresh");
}

export function clearAuthState({ correlationId, entrypoint = "logout", broadcast = true } = {}) {
    const currentSnapshot = readAuthSnapshot();
    const snapshot = createEmptySnapshot(buildNextMeta("logout", currentSnapshot));
    snapshot.storageName = currentSnapshot.storageName || "localStorage";
    snapshot.correlationId = correlationId;
    snapshot.entrypoint = entrypoint;
    return commitSnapshot(snapshot, "logout", { broadcast });
}

function normalizeBroadcastUserInfo(payload) {
    const userInfo = payload?.userInfo ?? payload?.user_info;
    if (typeof userInfo === "string") {
        return userInfo;
    }
    if (userInfo && typeof userInfo === "object") {
        return JSON.stringify(userInfo);
    }
    if (payload?.user && typeof payload.user === "object") {
        return JSON.stringify(payload.user);
    }
    return null;
}

function snapshotFromBroadcastMessage(message) {
    const payload = message?.snapshot || message?.payload || null;
    const action = normalizeAuthAction(message);
    if (!payload && action !== "logout") {
        return null;
    }
    const meta = normalizeSessionMeta(message.meta || payload?.meta || payload);
    if (!meta?.sessionEpoch) {
        return null;
    }
    if (action === "logout" || meta.status === LOGGED_OUT_STATUS) {
        return createEmptySnapshot(meta);
    }
    return {
        accessToken: payload.access_token || payload.accessToken || null,
        refreshToken: payload.refresh_token || payload.refreshToken || null,
        tokenExpiresAt: String(payload.token_expires_at || payload.tokenExpiresAt || ""),
        tokenIssuedAt: String(payload.token_issued_at || payload.tokenIssuedAt || ""),
        userInfo: normalizeBroadcastUserInfo(payload),
        activeOrganizationId: payload.active_organization_id || payload.activeOrganizationId || null,
        storageName: payload.storage || "localStorage",
        meta,
        status: meta.status === "degraded" ? "degraded" : AUTHENTICATED_STATUS,
    };
}

function normalizeAuthAction(message) {
    const rawStatus = message?.meta?.status || message?.snapshot?.authStatus || message?.snapshot?.status;
    if (rawStatus === LOGGED_OUT_STATUS || rawStatus === "logged_out") {
        return "logout";
    }
    const rawAction = String(message?.action || message?.event || message?.type || "refresh").toLowerCase();
    if (rawAction.includes("logout") || rawAction.includes("logged_out") || rawAction.includes("anonymous")) {
        return "logout";
    }
    if (rawAction.includes("login")) {
        return "login";
    }
    return "refresh";
}

function handleIncomingAuthMessage(message) {
    if (!message || typeof message !== "object" || message.tabId === getAuthTelemetryTabId()) {
        return;
    }
    if (message.type && message.type !== AUTH_STATE_EVENT_TYPE && !message.meta && !message.snapshot && !message.payload) {
        return;
    }
    const action = normalizeAuthAction(message);
    const incomingSnapshot = snapshotFromBroadcastMessage(message);
    if (!incomingSnapshot) {
        return;
    }
    const currentSnapshot = getCurrentSnapshotWithoutStorageRefresh();
    if (!shouldAcceptIncomingSnapshot(incomingSnapshot, currentSnapshot, action)) {
        return;
    }
    inMemorySnapshot = incomingSnapshot;
    notifyAuthStateListeners(incomingSnapshot, { source: "broadcast", action });
}

function handleStorageEvent(event) {
    if (!AUTH_STATE_KEYS.has(event.key)) {
        return;
    }
    const incomingSnapshot = readSnapshotFromStorage();
    const currentSnapshot = getCurrentSnapshotWithoutStorageRefresh();
    if (
        event.key === AUTH_STORAGE_KEYS.activeOrganizationId
        && incomingSnapshot.meta?.sessionEpoch === currentSnapshot.meta?.sessionEpoch
        && incomingSnapshot.activeOrganizationId !== currentSnapshot.activeOrganizationId
    ) {
        inMemorySnapshot = { ...currentSnapshot, activeOrganizationId: incomingSnapshot.activeOrganizationId };
        notifyAuthStateListeners(inMemorySnapshot, { source: "storage", action: "active-organization" });
        return;
    }
    const action = incomingSnapshot.status === LOGGED_OUT_STATUS ? "logout" : "refresh";
    if (!shouldAcceptIncomingSnapshot(incomingSnapshot, currentSnapshot, action)) {
        return;
    }
    inMemorySnapshot = incomingSnapshot;
    notifyAuthStateListeners(incomingSnapshot, { source: "storage", action });
}

export function ensureAuthStateSyncStarted() {
    if (!canUseWindow() || syncStarted) {
        return;
    }
    syncStarted = true;
    inMemorySnapshot = readSnapshotFromStorage();
    window.addEventListener("storage", handleStorageEvent);
    getBroadcastChannel();
}

export function subscribeAuthState(listener) {
    ensureAuthStateSyncStarted();
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function parseStoredUserInfo(snapshot = readAuthSnapshot()) {
    return parseJson(snapshot.userInfo);
}

ensureAuthStateSyncStarted();
